import { initI18n } from '@/lib/i18n';

import notice from '@/i18n/en/dpdp.json';

/**
 * The `dpdp` i18n namespace (BP-03 §9: "`dpdp.notice.*` (versioned), `dpdp.dsr.*`,
 * `dpdp.breach.*`").
 *
 * `@/i18n/en/dpdp.json` holds **only** the notice, and it is generated from
 * `app.modules.consent.notice.SECTIONS` — the exact 36 keys `notice.i18n_keys()` emits,
 * pinned by `api/tests/consent/test_notice_bundle.py`. Rule 7 derives
 * `notice_version` from that text, so the page a data principal reads and the version
 * recorded against their consent are the same document by construction; hand-editing
 * either half fails a test.
 *
 * The chrome around the notice — the DSR queue, the breach wizard, the retention panel —
 * is not versioned and is not part of the notice, so it lives here beside it rather than
 * in the generated file.
 *
 * The bundle is added to BP-01's i18next instance rather than to
 * `src/lib/i18n/resources.ts`: `lib/` is shared by both apps and knows nothing about
 * features, and `addResourceBundle` is exactly i18next's seam for a feature that brings
 * its own strings.
 */

/** The namespace name. */
export const DPDP_NAMESPACE = 'dpdp';

/** BP-03 §9's `dpdp.dsr.*`, `dpdp.breach.*` and `dpdp.retention.*`. */
export const chrome = {
  dsr: {
    title: 'Data requests',
    empty: 'No data requests yet.',
    client: 'Client',
    type: 'Request',
    due: 'Due',
    status: 'Status',
    daysLeft: '{{count}} days left',
    dueToday: 'Due today',
    overdue: '{{count}} days overdue',
    open: 'Open a request',
    openSubmit: 'Open request',
    receivedVia: 'How it arrived',
    verify: 'Verify the requester',
    verifyNote: 'How you checked',
    verified: 'Requester verified',
    fulfil: 'Fulfil',
    reject: 'Reject',
    cancel: 'Cancel',
    note: 'Note',
    download: 'Download the export',
    downloadHint: 'The link is valid for 15 minutes and is regenerated on each click.',
    exportReady: 'Export ready ({{count}} files)',
    correction: 'Correction',
    correctionApplied: 'Corrected: {{fields}}',
    erasureSkipped: 'Still to erase: {{modules}}',
    erasureDone: 'Erased across: {{modules}}',
    stillOpen: 'Kept open until every module can erase.',
    closed: 'Closed.',
    type_access: 'Copy of my data',
    type_correct: 'Correction',
    type_erase: 'Erase me',
  },
  breach: {
    title: 'Breach incidents',
    empty: 'No breach incidents. Good.',
    open: 'Record a breach',
    step1: '1. What happened',
    step2: '2. The notice your clients will get',
    step3: '3. The Board',
    incidentTitle: 'Short title',
    description: 'What happened',
    detectedAt: 'When you became aware',
    categories: 'What data was involved',
    affected: 'Affected client ids',
    mitigation: 'What you have already done',
    clientAction: 'What the client can do',
    generate: 'Generate the notice',
    notifyClients: 'Mark clients notified',
    boardReported: 'Mark the Board report filed',
    boardReference: "The Board's reference",
    close: 'Close the incident',
    postmortem: 'Postmortem',
    countdown: '{{hours}} h left to report to the Board',
    countdownOverdue: 'The 72-hour Board report is overdue by {{hours}} h',
    notified: 'Clients notified',
    filed: 'Board report filed',
    within72h: 'Filed inside 72 hours',
  },
  retention: {
    title: 'Retention',
    dryRun: 'Reporting only — nothing is deleted until dry run is turned off.',
    live: 'The nightly sweep is deleting.',
    inactive: 'Inactive {{months}} months, awaiting approval',
    inactiveEmpty: 'No client is due for review.',
    consentRows: 'Consent rows past their window: {{count}}',
    dsrRows: 'Requests past three years: {{count}}',
    exports: 'Export files past 30 days: {{count}}',
    breaches: 'Breach records past five years (kept): {{count}}',
    approve: 'Open an erase request',
  },
  privacy: {
    title: 'Privacy',
    version: 'Version {{version}}',
    grievance: 'Grievance officer',
    board: 'Data Protection Board of India',
    frontDesk:
      'Ask at the front desk and we will show you your consents, correct anything wrong, or start an erase request.',
    frontDeskTitle: 'Your data',
  },
  // BP-11 R17. Both stores require an app that can create an account to let
  // somebody delete it from inside the app, and the copy has to be honest
  // about the one thing people get wrong: the request is not instant, and
  // some of it the salon is required by law to keep.
  erase: {
    title: 'Delete my data',
    body: 'We will erase your name, number and your appointment history. Your invoices stay, because the GST rules require a salon to keep them — they will no longer carry your name.',
    action: 'Request deletion',
    asking: 'Sending…',
    confirm: 'This cannot be undone. Delete my data?',
    cancel: 'Keep my data',
    pending: 'Deletion requested on {{date}}. The salon has until {{due}} to complete it.',
    overdue:
      'Deletion was requested on {{date}} and is past its due date. You can complain to the Data Protection Board of India.',
    failed: 'That did not go through. Try again, or ask at the front desk.',
    signedOut:
      'Sign in on this phone and open Privacy to ask for your data to be deleted. If you cannot sign in, ask at the salon — either way we have to answer within ninety days.',
  },
} as const;

/** The bundle as i18next holds it: the generated notice plus the chrome above. */
export const dpdpBundle = { dpdp: { ...notice.dpdp, ...chrome } };

/**
 * Register the namespace on BP-01's i18next instance. Idempotent, and safe to call from
 * several screens: `addResourceBundle` replaces the bundle it already holds.
 */
export function registerDpdpI18n(): void {
  initI18n().addResourceBundle('en', DPDP_NAMESPACE, dpdpBundle, true, true);
}

registerDpdpI18n();
